import React, { useState, useEffect, useRef } from 'react';
import { Cpu, Brain, Zap, Server, Code, Sparkles } from 'lucide-react';
import { portfolioData } from '../data/mock';

const coreIcons = {
  AI: Brain,
  Automation: Zap,
  Infrastructure: Server,
  'Software Engineering': Code
};

const AIEra = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="aiera" ref={sectionRef} className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-500/5 to-transparent" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-violet-500/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transition-all duration-1000 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-xs font-mono mb-4">
              <Sparkles size={12} />
              <span>AI Era Engineering</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Engineering for the <span className="gradient-text">AI Era</span>
            </h2>
            <p className="text-gray-500 max-w-3xl mx-auto text-lg">{portfolioData.aiEra.description}</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
            {portfolioData.aiEra.core.map((item, i) => {
              const Icon = coreIcons[item.name] || Cpu;
              return (
                <div key={item.name} className="glass rounded-2xl p-6 text-center hover:border-violet-500/20 transition-all duration-500"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(20px)',
                    transitionDelay: `${i * 100}ms`,
                    transitionProperty: 'all',
                    transitionDuration: '0.6s',
                    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
                  }}>
                  <div className="text-3xl mb-3">{item.name === 'AI' ? '🤖' : item.name === 'Automation' ? '⚡' : item.name === 'Infrastructure' ? '🖥️' : '💻'}</div>
                  <h3 className="text-white font-semibold mb-2">{item.name}</h3>
                  <p className="text-gray-500 text-xs">{item.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="max-w-5xl mx-auto">
            <h3 className="text-white font-semibold text-lg mb-6 text-center">Current Areas of Focus</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {portfolioData.aiEra.areas.map((area, i) => (
                <div key={area} className="glass rounded-xl px-5 py-3 hover:border-violet-500/20 transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(10px)',
                    transitionDelay: `${i * 60}ms`,
                    transitionProperty: 'all',
                    transitionDuration: '0.4s',
                    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
                  }}>
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-violet-500 to-purple-500" />
                    <span className="text-gray-300 text-sm">{area}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIEra;
